import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  alpha,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  ShoppingBasket as ShoppingBasketIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import BillWritableTable from "../../../components/BillWritableTable";
import TableItem from "../../../components/TableItem";
import ReceivingProgressCard from "./ReceivingProgressCard";

/**
 * Enhanced OUTPUT items table with receiving and checking functionality
 *
 * Props:
 * - outputItems: Array of output line items
 * - setOutputItems: Function to update output items
 * - editOutput: Boolean for edit mode
 * - setEditOutput: Function to toggle edit mode
 * - onSave: Function to save edits
 * - canEdit: Boolean - can user edit
 * - onReceive: Function(lineItem) - open receive dialog
 * - onViewHistory: Function(lineItem) - open history dialog
 * - receivingProgress: Array of ReceivingProgressDTO
 * - columnsForOutput: Table columns definition
 * - writeable_page, writeable_rowsPerPage, etc: Pagination props
 */
function OutputItemsTable({
  outputItems,
  setOutputItems,
  editOutput,
  setEditOutput,
  onSave,
  canEdit,
  canReceive,
  onReceive,
  onViewHistory,
  receivingProgress,
  columnsForOutput,
  writeable_page,
  set_writeable_Page,
  writeable_rowsPerPage,
  set_writeable_RowsPerPage,
  categoryItems,
  onViewCategory,
  onSelectCategoryItems,
  onDeleteRow,
  outputItemCosts,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLineItem, setSelectedLineItem] = useState(null);

  const handleMenuOpen = (event, lineItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedLineItem(lineItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLineItem(null);
  };

  const handleReceive = () => {
    if (selectedLineItem) {
      onReceive(selectedLineItem);
    }
    handleMenuClose();
  };

  const handleViewHistory = () => {
    if (selectedLineItem) {
      onViewHistory(selectedLineItem);
    }
    handleMenuClose();
  };

  // Get progress for a specific line item
  const getProgress = (lineItemId) => {
    return receivingProgress?.find((p) => p.lineItemId === lineItemId);
  };

  return (
    <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ShoppingBasketIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Output Order Items
            </Typography>
            <Badge
              badgeContent={(outputItems?.length || 0) + (categoryItems?.length || 0)}
              color="primary"
              sx={{ ml: 1 }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            {!editOutput ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEditOutput(true)}
                disabled={!canEdit}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={onSave}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        {/* Receiving Progress Cards (if available) */}
        {receivingProgress && receivingProgress.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, fontWeight: 600 }}
            >
              Receiving Progress
            </Typography>
            {receivingProgress.map((progress) => (
              <ReceivingProgressCard
                key={progress.lineItemId}
                progress={progress}
                materialUnitCost={outputItemCosts?.[progress.lineItemId]?.materialUnitCost}
                totalCost={outputItemCosts?.[progress.lineItemId]?.totalCost}
                effectiveMaterials={outputItemCosts?.[progress.lineItemId]?.effectiveMaterials}
                calcSteps={outputItemCosts?.[progress.lineItemId]?.calcSteps}
                expectedQty={outputItemCosts?.[progress.lineItemId]?.expectedQty}
                isCategoryOutput={outputItemCosts?.[progress.lineItemId]?.isCategoryOutput}
              />
            ))}
          </Box>
        )}

        {/* Table */}
        {editOutput ? (
          <BillWritableTable
            columns={columnsForOutput}
            rows={outputItems}
            setRows={setOutputItems}
            page={writeable_page}
            setPage={set_writeable_Page}
            rowsPerPage={writeable_rowsPerPage}
            tablePagin={false}
            setRowsPerPage={set_writeable_RowsPerPage}
            onDeleteRow={onDeleteRow}
          />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  {columnsForOutput.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    style={{ fontSize: "0.8rem", fontWeight: 600 }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outputItems.map((row, index) => {
                  const progress = getProgress(row.lineId);
                  const remainingQty = Math.max(
                    (row.qty ?? 0) - (progress?.totalReceivedQty ?? 0),
                    0
                  );
                  return (
                    <TableRow
                      key={row.lineId || index}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      {columnsForOutput.map((column) => {
                        let value = row[column.id];
                        if (column.id === "availability") {
                          value = progress?.totalReceivedQty ?? 0;
                        }
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ fontSize: "0.8rem", padding: "10px" }}
                          >
                            {column.format && typeof value === "number"
                              ? column.format(value)
                              : value}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {canReceive ? (
                            <>
                              <Tooltip title="Receive Items">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onReceive({ ...row, remainingQty })}
                                >
                                  <LocalShippingIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View History">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => onViewHistory(row)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No items message */}
        {(!outputItems || outputItems.length === 0) && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No output items found
            </Typography>
          </Box>
        )}

        {/* Output Categories */}
        {categoryItems && categoryItems.length > 0 && (
          <Box sx={{ mt: outputItems && outputItems.length > 0 ? 3 : 0 }}>
            <Typography
              variant="body2"
              fontWeight="600"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Output Categories
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {editOutput ? "Select Items" : "View Items"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryItems.map((cat) => (
                    <TableRow key={cat.categoryId}>
                      <TableCell>{cat.categoryName}</TableCell>
                      <TableCell align="right">
                        {editOutput ? (
                          <Tooltip title="Select import items from this category">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() =>
                                onSelectCategoryItems &&
                                onSelectCategoryItems(
                                  cat.categoryId,
                                  cat.categoryName
                                )
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="View import items in this category">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                onViewCategory &&
                                onViewCategory(cat.categoryId, cat.categoryName)
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>

      {/* Context Menu (optional alternative) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleReceive}>
          <ListItemIcon>
            <LocalShippingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Receive Items</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewHistory}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}

export default OutputItemsTable;
